import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft, Tag, Check, Loader2, AlertCircle } from "lucide-react";
import { api, getErrorMessage } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";

const PLAN_LABELS = {
  free: "Free",
  starter: "Starter",
  basic: "Basic",
  pro: "Pro",
  professional: "Professional",
  business: "Business",
};

const PLAN_PRICES = {
  free: 0,
  starter: 299,
  basic: 299,
  pro: 799,
  professional: 799,
  business: 1499,
};

const PLAN_CHARS = {
  free: 20000,
  starter: 100000,
  basic: 100000,
  pro: 500000,
  professional: 500000,
  business: 1500000,
};

const TOPUP_BUNDLE_LABELS = {
  starter_50k: "50,000 characters",
  starter_100k: "100,000 characters",
  starter_200k: "200,000 characters",
  pro_100k: "100,000 characters",
  pro_500k: "500,000 characters",
  pro_1m: "1,000,000 characters",
};

const TOPUP_BUNDLE_PRICES = {
  starter_50k: 149,
  starter_100k: 249,
  starter_200k: 449,
  pro_100k: 199,
  pro_500k: 899,
  pro_1m: 1699,
};

const TOPUP_BUNDLE_CHARS = {
  starter_50k: 50000,
  starter_100k: 100000,
  starter_200k: 200000,
  pro_100k: 100000,
  pro_500k: 500000,
  pro_1m: 1000000,
};

function formatChars(n) {
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(0)}K`;
  return String(n);
}

export default function PaymentCheckout() {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state || {};

  const { type, plan, bundle_id, amount: customAmount, chars_per_bdt: cpb } = state;

  // Derive base values
  let label = "";
  let baseAmount = 0;
  let chars = 0;
  let contextKey = "all";

  if (type === "plan") {
    label = `${PLAN_LABELS[plan] || plan} Plan (monthly)`;
    baseAmount = PLAN_PRICES[plan] || 0;
    chars = PLAN_CHARS[plan] || 0;
    contextKey = plan;
  } else if (type === "topup") {
    label = `Top-up: ${TOPUP_BUNDLE_LABELS[bundle_id] || bundle_id}`;
    baseAmount = TOPUP_BUNDLE_PRICES[bundle_id] || 0;
    chars = TOPUP_BUNDLE_CHARS[bundle_id] || 0;
    contextKey = bundle_id;
  } else if (type === "custom") {
    const charsPerBdt = cpb || 200;
    chars = customAmount * charsPerBdt;
    baseAmount = customAmount;
    label = `Custom Top-up: ${formatChars(chars)} characters`;
    contextKey = "custom";
  }

  const [couponCode, setCouponCode] = useState("");
  const [couponState, setCouponState] = useState(null); // {valid, discount_amount, final_amount, discount_type, discount_value}
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponError, setCouponError] = useState("");
  const [loading, setLoading] = useState(false);

  const finalAmount = couponState ? couponState.final_amount : baseAmount;
  const discountAmount = couponState ? couponState.discount_amount : 0;

  async function handleValidateCoupon() {
    if (!couponCode.trim()) return;
    setCouponLoading(true);
    setCouponError("");
    setCouponState(null);
    try {
      const { data } = await api.post("/billing/validate-coupon", { code: couponCode.trim(), context_key: contextKey, amount: baseAmount });
      setCouponState(data);
    } catch (err) {
      setCouponError(getErrorMessage(err) || "Invalid coupon");
    } finally {
      setCouponLoading(false);
    }
  }

  async function handleProceed() {
    if (!type) return;
    setLoading(true);
    try {
      const body = { type };
      if (type === "plan") body.plan = plan;
      if (type === "topup") body.bundle_id = bundle_id;
      if (type === "custom") body.amount = customAmount;
      if (couponState) body.coupon_code = couponState.code || couponCode.trim().toUpperCase();

      const { data } = await api.post("/billing/initiate-payment", body);

      if (data.direct) {
        toast.success("Payment applied! Your account has been updated.");
        navigate("/dashboard/billing");
        return;
      }
      if (data.payment_url) {
        window.location.href = data.payment_url;
      }
    } catch (err) {
      toast.error(getErrorMessage(err) || "Could not initiate payment");
    } finally {
      setLoading(false);
    }
  }

  if (!type) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="text-gray-400">No checkout data. <button className="text-indigo-400 underline" onClick={() => navigate(-1)}>Go back</button></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-950 flex items-start justify-center px-4 pt-16 pb-24">
      <div className="w-full max-w-lg">
        {/* Back */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-400 hover:text-white mb-8 text-sm"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        <h1 className="text-2xl font-bold text-white mb-2">Order Summary</h1>
        <p className="text-gray-400 text-sm mb-8">Review your purchase before proceeding</p>

        {/* Item card */}
        <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6 mb-6 space-y-4">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-white font-semibold text-lg">{label}</p>
              {chars > 0 && (
                <p className="text-gray-400 text-sm mt-1">
                  {formatChars(chars)} characters &nbsp;·&nbsp;
                  ~{Math.floor(chars / 1000).toLocaleString()} articles
                </p>
              )}
            </div>
            <p className="text-white font-semibold text-lg">৳{baseAmount.toLocaleString()}</p>
          </div>

          {/* Divider */}
          <div className="border-t border-gray-800" />

          {/* Coupon input */}
          <div>
            <label className="text-gray-300 text-sm font-medium mb-2 block">Coupon code</label>
            <div className="flex gap-2">
              <Input
                placeholder="e.g. WELCOME20"
                value={couponCode}
                onChange={e => { setCouponCode(e.target.value.toUpperCase()); setCouponState(null); setCouponError(""); }}
                onKeyDown={e => e.key === "Enter" && handleValidateCoupon()}
                className="bg-gray-800 border-gray-700 text-white placeholder-gray-500 flex-1"
                disabled={!!couponState}
              />
              {couponState ? (
                <Button variant="outline" size="sm" onClick={() => { setCouponState(null); setCouponCode(""); setCouponError(""); }}
                  className="border-gray-700 text-gray-300 hover:text-white">
                  Remove
                </Button>
              ) : (
                <Button variant="outline" size="sm" onClick={handleValidateCoupon} disabled={couponLoading || !couponCode.trim()}
                  className="border-gray-700 text-gray-300 hover:text-white min-w-[80px]">
                  {couponLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Tag className="w-4 h-4 mr-1" />Apply</>}
                </Button>
              )}
            </div>
            {couponError && (
              <p className="text-red-400 text-xs mt-2 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" /> {couponError}
              </p>
            )}
            {couponState && (
              <p className="text-green-400 text-xs mt-2 flex items-center gap-1">
                <Check className="w-3 h-3" />
                {couponState.discount_type === "percentage"
                  ? `${couponState.discount_value}% off applied`
                  : `৳${couponState.discount_value} off applied`}
              </p>
            )}
          </div>

          {/* Totals */}
          <div className="space-y-2 text-sm">
            <div className="flex justify-between text-gray-400">
              <span>Subtotal</span>
              <span>৳{baseAmount.toLocaleString()}</span>
            </div>
            {discountAmount > 0 && (
              <div className="flex justify-between text-green-400">
                <span>Discount ({couponState.code})</span>
                <span>−৳{discountAmount.toLocaleString()}</span>
              </div>
            )}
            <div className="border-t border-gray-800 pt-2 flex justify-between text-white font-semibold text-base">
              <span>Total</span>
              <span>৳{finalAmount.toLocaleString()}</span>
            </div>
            {finalAmount <= 0 && (
              <p className="text-green-400 text-xs text-center pt-1">100% discount — no payment required!</p>
            )}
          </div>
        </div>

        {/* Proceed */}
        <Button
          onClick={handleProceed}
          disabled={loading}
          className="w-full h-12 bg-indigo-600 hover:bg-indigo-500 text-white font-semibold text-base rounded-xl"
        >
          {loading ? (
            <><Loader2 className="w-5 h-5 animate-spin mr-2" /> Processing…</>
          ) : finalAmount <= 0 ? (
            "Apply & Continue"
          ) : (
            `Pay ৳${finalAmount.toLocaleString()} →`
          )}
        </Button>

        <p className="text-center text-gray-500 text-xs mt-4">
          Secure payment via EPS
        </p>
      </div>
    </div>
  );
}
